import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { SearchDataSource } from 'src/featureFlags/featureFlags';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPageType } from 'src/redux/navigation/selectors';
import { adaptBrowseFilters } from 'src/redux/products/adapters/browse-data-api/adaptBrowseFilters';
import { adaptBrowseProducts } from 'src/redux/products/adapters/browse-data-api/adaptBrowseProducts';
import { adaptBrowseSortOptions } from 'src/redux/products/adapters/browse-data-api/adaptBrowseSortOptions';
import {
  CATEGORY_NOT_FOUND_ERROR,
  MISSING_CATEGORY_ID_ERROR,
  PARTNER_NOT_FOUND_ERROR,
} from 'src/redux/products/thunks/thunkErrors';
import type { RequestProductsActionPayload, TriggerName } from 'src/redux/products/types';
import {
  getCategoryListingsData,
  getPartnerListingsData,
  getSearchListingsData,
} from 'src/services/browse-data-api/getListingsData';
import { fetchListingsData } from 'src/services/listings-api/fetchListingsData';
import { PageType } from 'src/types/navigation';
import { logger } from 'src/utils/serverLogger';
import { adaptListingsProducts } from '../adapters/listings-api/adaptListingsProducts';
import { selectProductsQueryAsUrlQuery } from '../selectors/urlSelectors';

export interface RequestProductsOptions {
  cookie?: string;
  featureFlags?: FeatureFlags;
  requestIsLoadTest?: boolean;
  triggerName?: TriggerName;
}

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions
>(
  'products/requestProducts',
  async ({ cookie, featureFlags, requestIsLoadTest, triggerName }, { getState }) => {
    const state = getState() as ReduxApplicationState;
    const { page } = state.productsQuery;
    const { currencyCode, deliveryZone } = state.userConfiguration;
    const useListingsApi = featureFlags?.[SearchDataSource.NAME] === SearchDataSource.LISTINGS_API;
    const queryObject = selectProductsQueryAsUrlQuery(state, page);

    const commonGetDataArgs = {
      cookie,
      currencyCode,
      ...(deliveryZone && { deliveryZone }),
    };
    let response;

    switch (selectPageType(state)) {
      case PageType.Search: {
        response = useListingsApi
          ? await fetchListingsData({
              searchTerm: state.productsQuery.canonicalFilters.searchTerm!,
              userId: state.userConfiguration.gaVisitorId,
            })
          : await getSearchListingsData({
              ...commonGetDataArgs,
              query: queryObject,
              requestIsLoadTest,
              loggedInUserId: state.user.cognitoID || undefined,
              searchTerm: state.productsQuery.canonicalFilters.searchTerm!,
              userId: state.userConfiguration.gaVisitorId,
            });
        break;
      }
      case PageType.Category: {
        const { previewing, ...restOfQuery } = queryObject;
        const categoryId = state.productsQuery.canonicalFilters.categoryIDs?.[0]
          ? state.productsQuery.canonicalFilters.categoryIDs[0]
          : null;

        if (!categoryId) {
          logger.error('category ID missing for category listing page');

          throw MISSING_CATEGORY_ID_ERROR;
        }

        response = await getCategoryListingsData({
          ...commonGetDataArgs,
          categoryId,
          ...(previewing ? { previewing: true } : {}),
          query: restOfQuery, // when categoryId is set, fetchBrowseData will not send query.path
          loggedInUserId: state.user.cognitoID || undefined,
          userId: state.userConfiguration.gaVisitorId,
        });

        if (response === null) {
          throw CATEGORY_NOT_FOUND_ERROR;
        }

        break;
      }
      case PageType.Partner: {
        response = await getPartnerListingsData({
          ...commonGetDataArgs,
          partnerShortcode: state.productsQuery.canonicalFilters.partnerShortcode!,
          query: queryObject,
          loggedInUserId: state.user.cognitoID || undefined,
          userId: state.userConfiguration.gaVisitorId,
        });
        if (response === null) {
          throw PARTNER_NOT_FOUND_ERROR;
        }
        break;
      }
      default:
        console.error('unknown page type');
    }

    if (response?.redirectUrl) {
      return { redirectUrl: response.redirectUrl };
    }

    const payload: RequestProductsActionPayload = useListingsApi
      ? {
          facets: [],
          products: adaptListingsProducts({
            products: response.data.products,
            pagination: response.data.pagination,
            attributionToken: response.data.metadata.attributionToken,
            term: state.productsQuery.canonicalFilters.searchTerm!,
            currencyCode,
          }),
          sortOptions: [],
          triggerName,
        }
      : {
          facets: adaptBrowseFilters(response.data.filters),
          products: adaptBrowseProducts(response.data),
          sortOptions: adaptBrowseSortOptions(response.data.sortingOptions),
          triggerName,
        };

    return payload;
  },
);
